import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import {
    BehaviorSubject,
    catchError,
    map,
    Observable,
    of,
    switchMap,
    take,
} from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { UserDTO, AdminUserDTO } from './user-table.type';
import { Pagination } from 'app/modules/shared/models/Account';
import { UserService } from 'app/core/user/user.service';
import { SnackBarService } from 'app/core/snack-bar/snack-bar.service';

@Injectable({ providedIn: 'root' })
export class UserTableService {
    private readonly baseUrl = environment.baseUrl;
    private readonly repoUrl = environment.repoUrl;

    private _users: BehaviorSubject<UserDTO[] | null> = new BehaviorSubject(
        null,
    );

    private _pagination: BehaviorSubject<Pagination> = new BehaviorSubject(
        new Pagination(),
    );

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _snackBar: SnackBarService,
    ) {}

    /**
     * Getter for users
     */
    get users$(): Observable<UserDTO[]> {
        return this._users.asObservable();
    }

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<Pagination> {
        return this._pagination.asObservable();
    }

    /**
     * Get users with pagination
     */
    getUsers(
        query: string = '',
        page = 0,
        size = 20,
        // sort = 'login',
        sort = 'id',
        order = 'ASC',
    ): Observable<UserDTO[]> {
        const params: any = { page, size };
        // const params: any = { page };

        if (query) {
            params.any = query;
        }

        if (sort) {
            params.sort = [sort + ',' + order];
        }

        return this._httpClient
            .get<UserResponse>(`${this.baseUrl}/api/user/base-data`, { params })
            .pipe(
                map((data) => {
                    this._users.next(data.data);
                    this._pagination.next(data.metadata.pagination);
                    return data.data;
                }),
                catchError((error) => {
                    console.log(error);
                    if (error.status === 403) {
                        this._users.next([]);
                        this._pagination.next(new Pagination());
                        return of([]);
                    }
                }),
            );
    }

    /**
     * Get users
     */
    getPatients(
        userId: number,
        query: string = '',
        page = 0,
        size = 20,
        sort = '',
        order = 'ASC',
    ): Observable<UserDTO[]> {
        const params: any = { page, size };

        if (query) {
            params.query = query;
        }

        if (sort) {
            params.sort = [sort + ',' + order];
        }

        return this._httpClient
            .get<UserResponse>(`${this.baseUrl}/api/user/${userId}/patients`, {
                params,
            })
            .pipe(
                map((data) => {
                    this._users.next(data.data);
                    this._pagination.next(data.metadata.pagination);
                    return data.data;
                }),
                catchError((error) => {
                    console.log(error);
                    if (error.status === 403) {
                        this._users.next([]);
                        this._pagination.next(new Pagination());
                        return of([]);
                    }
                }),
            );
    }

    /**
     * Get users
     */
    // getUsers(): Observable<UserDTO[]> {
    //     return this._httpClient
    //         .get<UserDTO[]>(`${this.baseUrl}/api/admin/users/all`)
    //         .pipe(
    //             tap((users: UserDTO[]) => {
    //                 this._users.next(users);
    //             })
    //         );
    // }

    // TODO: explore/compare/discuss the different Endpoints for create/update/delete of users..
    //  Which one to use? (POST:) "/api/admin/users", "/api/user/registration/register", "/admin-api/user"
    //  -> all have different implications on the backend logic of a user
    /**
     * Create user
     */
    // createUser(userParams: UserDTO): Observable<any> {
    //     return this.users$.pipe(
    //         take(1),
    //         switchMap((users) =>
    //             this._httpClient
    //                 .post<any>(`${this.baseUrl}/api/admin/users`, userParams)
    //                 .pipe(
    //                     map((user) => {
    //                         // Update the userEntries with the new user
    //                         this._users.next([user, ...users]);

    //                         // Return the new user
    //                         return user;
    //                     }),
    //                     catchError((err, caught) => {
    //                         return of(false);
    //                     })
    //                 )
    //         )
    //     );
    // }

    createUser(userParams: AdminUserDTO): Observable<any> {
        return this.users$.pipe(
            take(1),
            switchMap((users) =>
                this._httpClient
                    .post<any>(`${this.repoUrl}/admin-api/user`, userParams)
                    .pipe(
                        map((user) => {
                            // Update the userEntries with the new user
                            this._users.next([user, ...users]);

                            // Return the new user
                            return user;
                        }),
                        catchError((err, caught) => {
                            this._snackBar.openErrorSnackBar(err);
                            return of(false);
                        }),
                    ),
            ),
        );
    }

    /**
     * Delete user
     */
    deleteUser(userId: number): Observable<boolean> {
        return this.users$.pipe(
            take(1),
            switchMap((users) =>
                this._httpClient
                    .delete(
                        `${this.repoUrl}/admin-api/account/all-data/${userId}`,
                    )
                    .pipe(
                        map((isDeleted: boolean) => {
                            // Find the index of the deleted user
                            const index = users.findIndex(
                                (item) => item.userId == userId,
                            );

                            // Delete the user
                            users.splice(index, 1);

                            // Update the users
                            this._users.next(users);

                            // Return the deleted status
                            return true;
                        }),
                    ),
            ),
        );
    }

    /**
     * Update user
     */
    updateUser(userParams: UserDTO, modifiedByLogin: string): Observable<any> {
        /*return this.users$.pipe(
            take(1),
            switchMap((users) =>
                this._httpClient.put<any>(
                    `${this.baseUrl}/api/admin/users`,
                    userParams
                )
                    .pipe(
                        map((updatedUser) => {

                            // Find the index of the updated user
                            const index = users.findIndex(item => item.id === userParams.id);

                            // Update the user
                            users[index] = updatedUser;

                            // Update the user
                            this._users.next(users);

                            // Return the updated user
                            return updatedUser;

                        }),
                        catchError((err, caught) => {
                            return of(false);
                        })
                    )
            )
        );*/
        return null;
    }

    createCareTaker(data): Observable<any> {
        const params = new HttpParams().set('amount', data.amount);
        return this.users$.pipe(
            take(1),
            switchMap((users) =>
                this._httpClient
                    .post<any>(
                        `${this.repoUrl}/admin-api/user/${data.caretakerId}/care-recipient`,
                        null,
                        { params },
                    )
                    .pipe(
                        map((user) => {
                            // Update the userEntries with the new user
                            // this._users.next([user, ...users]);

                            // Return the new user
                            return user;
                        }),
                        catchError((err, caught) => {
                            this._snackBar.openErrorSnackBar(err);
                            return of(false);
                        }),
                    ),
            ),
        );
    }

    // POST
    createQuickUser(userParams: AdminUserDTO): Observable<any> {
        return this._userService.activeUser$.pipe(
            take(1),
            switchMap((user) =>
                this._httpClient
                    .post<any>(
                        `${this.baseUrl}/api/user/registration/${user.userId}/care-recipient`,
                        userParams,
                    )
                    .pipe(
                        catchError((err, caught) => {
                            // console.log(err, caught);
                            this._snackBar.openErrorSnackBar(err);
                            return of(false);
                        }),
                    ),
            ),
        );
    }

    // /api/user/device/by-imei/{imei}
    deviceByIMEI(imei: string): Observable<any> {
        return this._httpClient
            .get<any>(`${this.baseUrl}/api/user/device/by-imei/${imei}`)
            .pipe(
                catchError((err, caught) => {
                    // console.log(err, caught);
                    this._snackBar.openErrorSnackBar(err);
                    return of(false);
                }),
            );
    }

    /**
     * Restore Deleted user
     */
    restoreDeletedUser(userId: number): Observable<any> {
        return this._httpClient.post(
            `${this.repoUrl}/admin-api/account/restore/${userId}/devices`,
            { userId },
        );
        // .pipe(
        //     map((restored: boolean) => {
        //         // Find the index of the deleted user
        //         const index = users.findIndex(
        //             (item) => item.userId == userId,
        //         );

        //         // Update the users
        //         this._users.next(users);

        //         // Return the deleted status
        //         return true;
        //     }),
        // ),
    }
}

export class UserResponse {
    metadata: {
        pagination: Pagination;
    };
    data: UserDTO[];
}
